import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { History, User, Calendar, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import { VersionHistoryEntry } from '@/types/soa';
import { getSOAVersionHistory } from '@/services/soaService';
import { useTenancy } from '@/contexts/TenancyContext';

interface SOAVersionHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SOAVersionHistoryModal: React.FC<SOAVersionHistoryModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { activeTenancy, userIdToName } = useTenancy();
  const [versionHistory, setVersionHistory] = useState<VersionHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedVersions, setExpandedVersions] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isOpen && activeTenancy?.id) {
      fetchVersionHistory();
    }
  }, [isOpen, activeTenancy?.id]);

  const fetchVersionHistory = async () => {
    if (!activeTenancy?.id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const history = await getSOAVersionHistory(activeTenancy.id);
      setVersionHistory(history);
    } catch (err) {
      console.error('Error fetching version history:', err);
      setError('Failed to load version history');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (versionKey: string) => {
    setExpandedVersions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(versionKey)) {
        newSet.delete(versionKey);
      } else {
        newSet.add(versionKey);
      }
      return newSet;
    });
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getUserDisplayName = (userId: string) => {
    if (!userId || userId === 'Unknown') return 'Unknown User';
    return userIdToName?.[userId] || userId;
  };

  const getUpdatesMadeCount = (updatesMade?: Record<string, any>) => {
    if (!updatesMade) return 0;
    return Object.keys(updatesMade).length;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-2xl! w-full max-h-[85vh] overflow-hidden rounded-xl shadow-lg border border-white/10 p-0!"
        aria-describedby={undefined}
      >
        <div className="flex flex-col h-full bg-card">
          <DialogHeader className="sticky top-0 z-20 py-6 border-b px-6 border-white/10 bg-gradient-to-b from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60">
            <DialogTitle className="text-foreground flex items-center gap-2">
              <History className="h-5 w-5" />
              SOA Version History
            </DialogTitle>
          </DialogHeader>

          <ScrollArea className="flex-1 px-6 py-4">
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <Skeleton className="h-6 w-24" />
                      <Skeleton className="h-4 w-32" />
                    </div>
                    <Skeleton className="h-4 w-48 mb-2" />
                    <Skeleton className="h-4 w-full" />
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <p className="text-destructive mb-4">{error}</p>
                <Button variant="outline" onClick={fetchVersionHistory}>
                  Retry
                </Button>
              </div>
            ) : versionHistory.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
                <History className="h-12 w-12 mb-4 opacity-50" />
                <p>No version history available</p>
              </div>
            ) : (
              <div className="space-y-3">
                {versionHistory.map((version, index) => {
                  const isExpanded = expandedVersions.has(version.versionKey);
                  const updatesCount = getUpdatesMadeCount(version.updatesMade);
                  const isLatest = index === 0;

                  return (
                    <div
                      key={version.versionKey}
                      className={`p-4 border rounded-lg transition-colors ${
                        isLatest ? 'border-primary/50 bg-primary/5' : 'border-border hover:border-primary/30'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant={isLatest ? 'default' : 'secondary'}>
                              v{version.versionNumber}
                            </Badge>
                            {isLatest && (
                              <Badge variant="outline" className="text-xs">
                                Current
                              </Badge>
                            )}
                          </div>

                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3.5 w-3.5" />
                              {formatDate(version.dateUpdated)}
                            </span>
                            <span className="flex items-center gap-1">
                              <User className="h-3.5 w-3.5" />
                              {getUserDisplayName(version.updatedBy)}
                            </span>
                          </div>

                          {version.updateComments && (
                            <p className="mt-2 text-sm text-foreground">
                              {version.updateComments}
                            </p>
                          )}
                        </div>

                        {updatesCount > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleExpanded(version.versionKey)}
                            className="shrink-0"
                          >
                            <FileText className="h-4 w-4 mr-1" />
                            {updatesCount} change{updatesCount !== 1 ? 's' : ''}
                            {isExpanded ? (
                              <ChevronUp className="h-4 w-4 ml-1" />
                            ) : (
                              <ChevronDown className="h-4 w-4 ml-1" />
                            )}
                          </Button>
                        )}
                      </div>

                      {isExpanded && version.updatesMade && (
                        <div className="mt-4 pt-4 border-t border-border/50">
                          <p className="text-xs font-medium text-muted-foreground mb-2">
                            Controls Updated:
                          </p>
                          <div className="space-y-2">
                            {Object.entries(version.updatesMade).map(([controlId, controlData]) => (
                              <div
                                key={controlId}
                                className="text-sm p-2 bg-muted/50 rounded"
                              >
                                <span className="font-medium">
                                  {(controlData as any)?.controlID || controlId}
                                </span>
                                {(controlData as any)?.controlName && (
                                  <span className="text-muted-foreground ml-2">
                                    - {(controlData as any).controlName}
                                  </span>
                                )}
                                {(controlData as any)?.implementationStatus && (
                                  <Badge variant="outline" className="ml-2 text-xs">
                                    {(controlData as any).implementationStatus}
                                  </Badge>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>

          <div className="sticky bottom-0 z-20 p-4 border-t border-white/10 bg-gradient-to-t from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60">
            <Button variant="outline" onClick={onClose} className="w-full">
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SOAVersionHistoryModal;
